import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { DEFAULT_CONTENT } from "@/lib/content-defaults";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ImageUpload } from "@/components/ImageUpload";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/")({ component: ContentEditor });

const SECTIONS = ["home", "about", "programs", "support", "registration"] as const;

function ContentEditor() {
  const [section, setSection] = useState<(typeof SECTIONS)[number]>("home");
  const [data, setData] = useState<any>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.content.get(section).then((content) => {
      const override = content && typeof content === "object" && !Array.isArray(content) ? content : {};
      setData({ ...DEFAULT_CONTENT[section], ...override });
    });
  }, [section]);

  async function save() {
    setSaving(true);
    try {
      await api.content.set(section, data);
      toast.success("נשמר בהצלחה");
    } catch {
      toast.error("שמירה נכשלה");
    } finally {
      setSaving(false);
    }
  }

  function resetDefaults() {
    if (!confirm("לאפס לערכי ברירת המחדל?")) return;
    setData({ ...DEFAULT_CONTENT[section] });
  }

  if (!data) return <div>טוען...</div>;

  return (
    <div className="max-w-4xl">
      <h1 className="font-display text-3xl font-bold text-primary mb-2">עריכת תוכן</h1>
      <p className="text-muted-foreground mb-6">ערוך את הטקסטים בכל עמוד. השינויים יופיעו באתר מיד לאחר שמירה.</p>

      <Tabs value={section} onValueChange={(v) => setSection(v as any)}>
        <TabsList className="mb-6">
          <TabsTrigger value="home">בית</TabsTrigger>
          <TabsTrigger value="about">אודות</TabsTrigger>
          <TabsTrigger value="programs">ארגז הכלים</TabsTrigger>
          <TabsTrigger value="support">טיפולים</TabsTrigger>
          <TabsTrigger value="registration">הרשמה</TabsTrigger>
        </TabsList>

        <TabsContent value={section}>
          <div className="rounded-lg bg-card p-6 shadow-card border border-border space-y-4">
            {renderFields(data, setData)}
            <div className="flex gap-2 pt-4 border-t">
              <Button onClick={save} disabled={saving}>{saving ? "שומר..." : "שמור שינויים"}</Button>
              <Button variant="outline" onClick={resetDefaults}>אפס לברירת מחדל</Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function isImageKey(key: string) {
  return /image|photo|picture|cover/i.test(key);
}

function renderFields(data: any, setData: (d: any) => void) {
  return Object.entries(data).map(([key, val]) => {
    if (typeof val === "string") {
      if (isImageKey(key)) {
        return (
          <div key={key}>
            <Label>{key}</Label>
            <div className="mt-2">
              <ImageUpload value={val} onChange={(url) => setData({ ...data, [key]: url })} />
            </div>
          </div>
        );
      }
      const isLong = val.length > 80 || val.includes("\n");
      return (
        <div key={key}>
          <Label htmlFor={key}>{key}</Label>
          {isLong ? (
            <Textarea id={key} rows={Math.min(12, val.split("\n").length + 2)} value={val} onChange={(e) => setData({ ...data, [key]: e.target.value })} />
          ) : (
            <Input id={key} value={val} onChange={(e) => setData({ ...data, [key]: e.target.value })} />
          )}
        </div>
      );
    }
    if (Array.isArray(val)) {
      return (
        <div key={key}>
          <Label>{key}</Label>
          <div className="space-y-3 mt-2">
            {val.map((item: any, i: number) => (
              <div key={i} className="rounded border border-border p-3 bg-secondary/30 space-y-2">
                {typeof item === "string" ? (
                  <Textarea value={item} rows={3} onChange={(e) => {
                    const next = [...val]; next[i] = e.target.value;
                    setData({ ...data, [key]: next });
                  }} />
                ) : (
                  Object.entries(item).map(([k, v]) => (
                    <div key={k}>
                      <Label className="text-xs">{k}</Label>
                      {isImageKey(k) ? (
                        <div className="mt-1">
                          <ImageUpload value={(v as string) || ""} onChange={(url) => {
                            const next = [...val]; next[i] = { ...item, [k]: url };
                            setData({ ...data, [key]: next });
                          }} />
                        </div>
                      ) : (
                        <Input value={v as string} onChange={(e) => {
                          const next = [...val]; next[i] = { ...item, [k]: e.target.value };
                          setData({ ...data, [key]: next });
                        }} />
                      )}
                    </div>
                  ))
                )}
                <div className="flex gap-2 pt-1">
                  {typeof item === "object" && !("image" in item) && (
                    <Button size="sm" variant="outline" onClick={() => {
                      const next = [...val]; next[i] = { ...item, image: "" };
                      setData({ ...data, [key]: next });
                    }}>+ הוסף תמונה לפריט</Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  });
}
